import { Decimal } from "decimal.js";
import { z } from "zod";

// ─────────────────────────────── Cede Arena clans ─────────────────────────────
//
// A clan is an admin-created group a player belongs to, one at a time. It shows as
// a tag next to the player's name and can host a clan tournament: a start/end
// window ranked on the weekly leaderboard's formula, paying a per-rank breakdown
// into the arena balance when the window closes.
//
// Kept out of the cede-arena barrel's own file so the CMS and API can import the
// schemas without pulling the whole index.

export const ARENA_CLAN_NAME_MIN = 2;
export const ARENA_CLAN_NAME_MAX = 32;
/**
 * A join code is a standing secret: it is what an invite link carries, it is never rotated on its
 * own, and a wrong one is refused with its own error code. Short codes are guessable across many
 * accounts, which the per-user join cap does not bound, so the floor is the thing that stops it.
 */
export const ARENA_CLAN_PASSWORD_MIN = 8;
export const ARENA_CLAN_PASSWORD_MAX = 64;
export const ARENA_CLAN_MAX_MEMBERS_DEFAULT = 50;
/** Ceiling on a clan's configured capacity: the public roster is unpaginated, so it must stay renderable. */
export const ARENA_CLAN_MAX_MEMBERS_CEILING = 1_000;
/** Ceiling on authored reward brackets. */
export const ARENA_CLAN_TOURNAMENT_MAX_BRACKETS = 50;
/** Ceiling on a tournament's total payout (sum of reward × ranks); above this the breakdown is a typo. */
export const ARENA_CLAN_TOURNAMENT_MAX_TOTAL_REWARD_USDT = 10_000;
/** How long a settled tournament keeps showing on the clan page after it ends. */
export const ARENA_CLAN_SETTLED_TOURNAMENT_VISIBLE_DAYS = 7;

/** A positive USDT decimal string (up to 6 dp, the ledger's precision). */
export const ARENA_CLAN_USDT_REGEX = /^(?:0|[1-9]\d*)(?:\.\d{1,6})?$/;
const ClanUsdtSchema = z.string().regex(ARENA_CLAN_USDT_REGEX, {
  error: "must be a non-negative decimal with up to 6 decimals",
});

export const ARENA_CLAN_ROLE = {
  MEMBER: "MEMBER",
  CHIEF: "CHIEF",
} as const;
export type ARENA_CLAN_ROLE = (typeof ARENA_CLAN_ROLE)[keyof typeof ARENA_CLAN_ROLE];
export const ArenaClanRoleSchema = z.enum([ARENA_CLAN_ROLE.MEMBER, ARENA_CLAN_ROLE.CHIEF]);

/** What a clan tournament ranks on. Both use the weekly leaderboard's own sort; only the lead metric swaps. */
export const ARENA_CLAN_TOURNAMENT_RANKING = {
  /** Gross competition rewards credited in the window (the weekly board's paid-board metric). */
  WINNINGS: "WINNINGS",
  /** Competitions placed first in the window. */
  WINS: "WINS",
} as const;
export type ARENA_CLAN_TOURNAMENT_RANKING =
  (typeof ARENA_CLAN_TOURNAMENT_RANKING)[keyof typeof ARENA_CLAN_TOURNAMENT_RANKING];
export const ArenaClanTournamentRankingSchema = z.enum([
  ARENA_CLAN_TOURNAMENT_RANKING.WINNINGS,
  ARENA_CLAN_TOURNAMENT_RANKING.WINS,
]);

/** Derived from the window and `settledAt`; never stored. */
export const ARENA_CLAN_TOURNAMENT_STATUS = {
  UPCOMING: "UPCOMING",
  ONGOING: "ONGOING",
  /** The window closed; the settlement sweep has not paid it yet. */
  ENDED: "ENDED",
  SETTLED: "SETTLED",
} as const;
export type ARENA_CLAN_TOURNAMENT_STATUS =
  (typeof ARENA_CLAN_TOURNAMENT_STATUS)[keyof typeof ARENA_CLAN_TOURNAMENT_STATUS];
export const ArenaClanTournamentStatusSchema = z.enum([
  ARENA_CLAN_TOURNAMENT_STATUS.UPCOMING,
  ARENA_CLAN_TOURNAMENT_STATUS.ONGOING,
  ARENA_CLAN_TOURNAMENT_STATUS.ENDED,
  ARENA_CLAN_TOURNAMENT_STATUS.SETTLED,
]);

/**
 * One reward bracket: every rank in `[rankFrom, rankTo]` (inclusive) is paid
 * `rewardUsdt` each. Brackets must not overlap; gaps are allowed and pay nothing.
 */
export const ArenaClanRewardBracketSchema = z.object({
  rankFrom: z.number().int().positive(),
  rankTo: z.number().int().positive(),
  // Zod keeps running refines after a failed check, so guard the Decimal parse behind the regex.
  rewardUsdt: ClanUsdtSchema.refine((value) => !ARENA_CLAN_USDT_REGEX.test(value) || new Decimal(value).gt(0), {
    error: "reward must be greater than 0",
  }),
});
export type ArenaClanRewardBracket = z.infer<typeof ArenaClanRewardBracketSchema>;

const bracketsOverlap = (a: ArenaClanRewardBracket, b: ArenaClanRewardBracket): boolean =>
  a.rankFrom <= b.rankTo && b.rankFrom <= a.rankTo;

/** The most a breakdown can pay: every rank of every bracket filled. */
export function arenaClanRewardBreakdownTotal(brackets: readonly ArenaClanRewardBracket[]): Decimal {
  return brackets.reduce(
    (total, bracket) =>
      total.plus(new Decimal(bracket.rewardUsdt).times(Math.max(0, bracket.rankTo - bracket.rankFrom + 1))),
    new Decimal(0),
  );
}

export const ArenaClanRewardBreakdownSchema = z
  .array(ArenaClanRewardBracketSchema)
  .min(1)
  .max(ARENA_CLAN_TOURNAMENT_MAX_BRACKETS)
  .refine((brackets) => brackets.every((bracket) => bracket.rankTo >= bracket.rankFrom), {
    error: "a bracket's last rank must not be below its first",
  })
  .refine((brackets) => brackets.every((a, i) => brackets.every((b, j) => i === j || !bracketsOverlap(a, b))), {
    error: "reward brackets must not overlap",
  })
  // Element issues are already reported; only total a breakdown whose amounts parse.
  .refine(
    (brackets) =>
      !brackets.every((bracket) => ARENA_CLAN_USDT_REGEX.test(bracket.rewardUsdt)) ||
      arenaClanRewardBreakdownTotal(brackets).lte(ARENA_CLAN_TOURNAMENT_MAX_TOTAL_REWARD_USDT),
    {
      error: `total payout must not exceed ${ARENA_CLAN_TOURNAMENT_MAX_TOTAL_REWARD_USDT} USDT`,
    },
  );
export type ArenaClanRewardBreakdown = z.infer<typeof ArenaClanRewardBreakdownSchema>;

/** The amount rank `rank` pays under this breakdown, or null when no bracket covers it. */
export function arenaClanRewardForRank(breakdown: ArenaClanRewardBreakdown, rank: number): string | null {
  const bracket = breakdown.find((candidate) => rank >= candidate.rankFrom && rank <= candidate.rankTo);
  return bracket?.rewardUsdt ?? null;
}

// ─────────────────────────────── Arena-facing DTOs ────────────────────────────

/** A clan's chief as shown on the card and the header. */
export const ArenaClanChiefDtoSchema = z.object({
  hubUserId: z.number().int().positive(),
  nickname: z.string().nullable(),
});
export type ArenaClanChiefDto = z.infer<typeof ArenaClanChiefDtoSchema>;

/** One clan on the "all clans" list. Only active clans are listed. */
export const ArenaClanSummaryDtoSchema = z.object({
  id: z.number().int().positive(),
  /** URL-safe identity, derived from the name at creation and stable afterwards. */
  slug: z.string(),
  name: z.string(),
  logoUrl: z.string().nullable(),
  isPrivate: z.boolean(),
  memberCount: z.number().int().nonnegative(),
  maxMembers: z.number().int().positive(),
  chief: ArenaClanChiefDtoSchema.nullable(),
  /** A tournament whose window is open right now. */
  hasOngoingTournament: z.boolean(),
});
export type ArenaClanSummaryDto = z.infer<typeof ArenaClanSummaryDtoSchema>;

export const ArenaClanMemberDtoSchema = z.object({
  hubUserId: z.number().int().positive(),
  nickname: z.string().nullable(),
  role: ArenaClanRoleSchema,
  joinedAt: z.coerce.date(),
  /** Lifetime gross competition rewards (decimal USDT), the weekly board's winnings with no window. */
  totalRewardsUsdt: z.string(),
});
export type ArenaClanMemberDto = z.infer<typeof ArenaClanMemberDtoSchema>;

/** One ranked member of a clan tournament. Live standings and frozen results share this shape. */
export const ArenaClanTournamentStandingDtoSchema = z.object({
  rank: z.number().int().positive(),
  hubUserId: z.number().int().positive(),
  nickname: z.string().nullable(),
  rewardsWonUsdt: z.string(),
  wins: z.number().int().nonnegative(),
  played: z.number().int().nonnegative(),
  /** What this rank pays under the breakdown, or null when no bracket covers it. */
  rewardUsdt: z.string().nullable(),
});
export type ArenaClanTournamentStandingDto = z.infer<typeof ArenaClanTournamentStandingDtoSchema>;

export const ArenaClanTournamentDtoSchema = z.object({
  id: z.number().int().positive(),
  name: z.string(),
  startsAt: z.coerce.date(),
  endsAt: z.coerce.date(),
  rankingMode: ArenaClanTournamentRankingSchema,
  // Uncapped, so lowering the save cap never rejects a stored breakdown.
  rewardBreakdown: z.array(ArenaClanRewardBracketSchema),
  status: ArenaClanTournamentStatusSchema,
  settledAt: z.coerce.date().nullable(),
  /**
   * Ranked members. Live (computed at read time) until settled, then the frozen
   * results. Only members with at least one finalized competition in the window rank.
   */
  standings: z.array(ArenaClanTournamentStandingDtoSchema),
});
export type ArenaClanTournamentDto = z.infer<typeof ArenaClanTournamentDtoSchema>;

/** Response for `GET /v1/cede-arena/clans/:slug`. */
export const GetArenaClanResponseSchema = ArenaClanSummaryDtoSchema.extend({
  /** Chief first, then lifetime rewards descending. */
  members: z.array(ArenaClanMemberDtoSchema),
  /**
   * The clan's current tournament (upcoming or ongoing), else the most recent one
   * settled within `ARENA_CLAN_SETTLED_TOURNAMENT_VISIBLE_DAYS`, else null.
   */
  tournament: ArenaClanTournamentDtoSchema.nullable(),
});
export type GetArenaClanResponse = z.infer<typeof GetArenaClanResponseSchema>;

/** Response for `GET /v1/cede-arena/clans`: active clans, biggest first. */
export const GetArenaClansResponseSchema = z.object({
  clans: z.array(ArenaClanSummaryDtoSchema),
});
export type GetArenaClansResponse = z.infer<typeof GetArenaClansResponseSchema>;

/** The viewer's own membership, for the Join/Leave buttons and the "your clan" pill. */
export const ArenaClanMembershipDtoSchema = z.object({
  clanId: z.number().int().positive(),
  slug: z.string(),
  name: z.string(),
  role: ArenaClanRoleSchema,
  joinedAt: z.coerce.date(),
  /** False once Solo retired the clan: it is hidden and closed to joins, and the member can only leave. */
  isActive: z.boolean(),
});
export type ArenaClanMembershipDto = z.infer<typeof ArenaClanMembershipDtoSchema>;

/** Response for `GET /v1/cede-arena/clans/me`. */
export const GetMyArenaClanResponseSchema = z.object({
  membership: ArenaClanMembershipDtoSchema.nullable(),
  /**
   * When the viewer may join a clan again, or null when nothing blocks them. Derived
   * at read time from their latest leave and the live cooldown setting.
   */
  rejoinAvailableAt: z.coerce.date().nullable(),
  /** The live cooldown setting in hours, so the leave confirmation can say what leaving costs. */
  rejoinCooldownHours: z.number().int().nonnegative(),
});
export type GetMyArenaClanResponse = z.infer<typeof GetMyArenaClanResponseSchema>;

/**
 * Response for `GET /v1/cede-arena/clans/:slug/invite-link`, the chief-only read behind
 * the share button. The client builds the URL itself so preview deployments keep working.
 */
export const GetArenaClanInviteLinkResponseSchema = z.object({
  /** The clan's join code, or null on a public clan, whose invite link carries no code. */
  joinCode: z.string().nullable(),
});
export type GetArenaClanInviteLinkResponse = z.infer<typeof GetArenaClanInviteLinkResponseSchema>;

/** Body for `POST /v1/cede-arena/clans/:slug/join`. */
export const JoinArenaClanBodySchema = z.object({
  /** Required for a private clan; ignored for a public one. */
  password: z.string().max(ARENA_CLAN_PASSWORD_MAX).optional(),
});
export type JoinArenaClanBody = z.infer<typeof JoinArenaClanBodySchema>;

/** One player's clan, for the tag next to their name. */
export const ArenaClanBadgeDtoSchema = z.object({
  hubUserId: z.number().int().positive(),
  clanId: z.number().int().positive(),
  slug: z.string(),
  name: z.string(),
});
export type ArenaClanBadgeDto = z.infer<typeof ArenaClanBadgeDtoSchema>;

/**
 * Response for `GET /v1/cede-arena/clans/badges`: every current membership of an
 * active clan. One shared request backs every clan tag in the app, like the weekly
 * crowns. Bounded by the per-clan caps.
 */
export const GetArenaClanBadgesResponseSchema = z.object({
  badges: z.array(ArenaClanBadgeDtoSchema),
});
export type GetArenaClanBadgesResponse = z.infer<typeof GetArenaClanBadgesResponseSchema>;

/**
 * Error codes the clan endpoints return in the RFC 9457 `type` field. The API's
 * `ArenaClanErrorCode` derives from this, so the two cannot drift.
 */
export const ARENA_CLAN_ERROR_CODE = {
  CLANS_DISABLED: "ARENA_CLAN_CLANS_DISABLED",
  CLAN_NOT_FOUND: "ARENA_CLAN_CLAN_NOT_FOUND",
  CLAN_INACTIVE: "ARENA_CLAN_CLAN_INACTIVE",
  CLAN_FULL: "ARENA_CLAN_CLAN_FULL",
  INVALID_PASSWORD: "ARENA_CLAN_INVALID_PASSWORD",
  NOT_CLAN_CHIEF: "ARENA_CLAN_NOT_CLAN_CHIEF",
  CHIEF_CANNOT_LEAVE: "ARENA_CLAN_CHIEF_CANNOT_LEAVE",
  ALREADY_IN_CLAN: "ARENA_CLAN_ALREADY_IN_CLAN",
  NOT_IN_CLAN: "ARENA_CLAN_NOT_IN_CLAN",
  REJOIN_COOLDOWN: "ARENA_CLAN_REJOIN_COOLDOWN",
  MEMBER_NOT_FOUND: "ARENA_CLAN_MEMBER_NOT_FOUND",
  CLAN_INVALID: "ARENA_CLAN_CLAN_INVALID",
  CLAN_NAME_TAKEN: "ARENA_CLAN_CLAN_NAME_TAKEN",
  TOURNAMENT_NOT_FOUND: "ARENA_CLAN_TOURNAMENT_NOT_FOUND",
  TOURNAMENT_INVALID: "ARENA_CLAN_TOURNAMENT_INVALID",
  TOURNAMENT_OVERLAP: "ARENA_CLAN_TOURNAMENT_OVERLAP",
  TOURNAMENT_SETTLED: "ARENA_CLAN_TOURNAMENT_SETTLED",
  CONFIG_MISSING: "ARENA_CLAN_CONFIG_MISSING",
  CONFIG_INVALID: "ARENA_CLAN_CONFIG_INVALID",
} as const;
export type ARENA_CLAN_ERROR_CODE = (typeof ARENA_CLAN_ERROR_CODE)[keyof typeof ARENA_CLAN_ERROR_CODE];

// ──────────────────────────────────── CMS DTOs ────────────────────────────────

/** Global clan settings, backed by the singleton `arena_clan_configs` row. */
export const ArenaClanConfigDtoSchema = z.object({
  /**
   * Hours a player must wait after leaving (or being removed from) a clan before
   * joining one. Read live on every join, so editing it moves every pending cooldown.
   * 0 disables the wait.
   */
  rejoinCooldownHours: z
    .number()
    .int()
    .nonnegative()
    .max(24 * 365),
});
export type ArenaClanConfigDto = z.infer<typeof ArenaClanConfigDtoSchema>;

/** Response for `GET /v1/cms/arena-clan-settings`. */
export const GetArenaClanConfigResponseSchema = ArenaClanConfigDtoSchema;
export type GetArenaClanConfigResponse = z.infer<typeof GetArenaClanConfigResponseSchema>;

/** Body for `PUT /v1/cms/arena-clan-settings`. */
export const UpdateArenaClanConfigBodySchema = ArenaClanConfigDtoSchema;
export type UpdateArenaClanConfigBody = z.infer<typeof UpdateArenaClanConfigBodySchema>;

/** A clan as Solo sees it: the password is readable so ops can hand it to the KOL. */
export const CmsArenaClanDtoSchema = z.object({
  id: z.number().int().positive(),
  slug: z.string(),
  name: z.string(),
  logoUrl: z.string().nullable(),
  isPrivate: z.boolean(),
  /** Plaintext join code. Null on a public clan. */
  password: z.string().nullable(),
  maxMembers: z.number().int().positive(),
  /** Inactive clans are hidden on Arena and closed to joins; members keep their rows. */
  isActive: z.boolean(),
  memberCount: z.number().int().nonnegative(),
  chief: ArenaClanChiefDtoSchema.nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});
export type CmsArenaClanDto = z.infer<typeof CmsArenaClanDtoSchema>;

/** Body for `POST /v1/cms/arena-clans/save`. `id` absent creates; present updates. */
export const SaveCmsArenaClanBodySchema = z
  .object({
    id: z.number().int().positive().optional(),
    name: z.string().trim().min(ARENA_CLAN_NAME_MIN).max(ARENA_CLAN_NAME_MAX),
    logoUrl: z.string().trim().min(1).nullable(),
    isPrivate: z.boolean(),
    password: z.string().trim().min(ARENA_CLAN_PASSWORD_MIN).max(ARENA_CLAN_PASSWORD_MAX).nullable(),
    maxMembers: z.number().int().positive().max(ARENA_CLAN_MAX_MEMBERS_CEILING),
    isActive: z.boolean(),
  })
  .refine((body) => !body.isPrivate || body.password != null, {
    error: "a private clan needs a password",
    path: ["password"],
  });
export type SaveCmsArenaClanBody = z.infer<typeof SaveCmsArenaClanBodySchema>;

/** Response for `GET /v1/cms/arena-clans/get-by-page`: newest first. */
export const ListCmsArenaClansResponseSchema = z.object({
  items: CmsArenaClanDtoSchema.array(),
  total: z.number().int().nonnegative(),
  page: z.number().int().nonnegative(),
  size: z.number().int().positive(),
});
export type ListCmsArenaClansResponse = z.infer<typeof ListCmsArenaClansResponseSchema>;

export const CmsArenaClanMemberDtoSchema = z.object({
  hubUserId: z.number().int().positive(),
  nickname: z.string().nullable(),
  /** Wallet, or null for email-only accounts. */
  defiAddress: z.string().nullable(),
  role: ArenaClanRoleSchema,
  joinedAt: z.coerce.date(),
});
export type CmsArenaClanMemberDto = z.infer<typeof CmsArenaClanMemberDtoSchema>;

/** Response for `GET /v1/cms/arena-clans/members/get-by-page`: oldest join first. */
export const ListCmsArenaClanMembersResponseSchema = z.object({
  items: CmsArenaClanMemberDtoSchema.array(),
  total: z.number().int().nonnegative(),
  page: z.number().int().nonnegative(),
  size: z.number().int().positive(),
});
export type ListCmsArenaClanMembersResponse = z.infer<typeof ListCmsArenaClanMembersResponseSchema>;

/** Body for `POST /v1/cms/arena-clans/assign-chief`. The previous chief becomes a member. */
export const AssignCmsArenaClanChiefBodySchema = z.object({
  clanId: z.number().int().positive(),
  hubUserId: z.number().int().positive(),
});
export type AssignCmsArenaClanChiefBody = z.infer<typeof AssignCmsArenaClanChiefBodySchema>;

/** Body for `POST /v1/cms/arena-clans/remove-member`. The removed member is subject to the rejoin cooldown. */
export const RemoveCmsArenaClanMemberBodySchema = z.object({
  clanId: z.number().int().positive(),
  hubUserId: z.number().int().positive(),
});
export type RemoveCmsArenaClanMemberBody = z.infer<typeof RemoveCmsArenaClanMemberBodySchema>;

export const CmsArenaClanTournamentDtoSchema = z.object({
  id: z.number().int().positive(),
  clanId: z.number().int().positive(),
  clanName: z.string(),
  name: z.string(),
  startsAt: z.coerce.date(),
  endsAt: z.coerce.date(),
  rankingMode: ArenaClanTournamentRankingSchema,
  // Uncapped, so lowering the save cap never rejects a stored breakdown.
  rewardBreakdown: z.array(ArenaClanRewardBracketSchema),
  status: ArenaClanTournamentStatusSchema,
  settledAt: z.coerce.date().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});
export type CmsArenaClanTournamentDto = z.infer<typeof CmsArenaClanTournamentDtoSchema>;

/** Response for `GET /v1/cms/arena-clan-tournaments/get-by-id`: the tournament plus its live or frozen standings. */
export const GetCmsArenaClanTournamentResponseSchema = CmsArenaClanTournamentDtoSchema.extend({
  standings: z.array(ArenaClanTournamentStandingDtoSchema),
});
export type GetCmsArenaClanTournamentResponse = z.infer<typeof GetCmsArenaClanTournamentResponseSchema>;

/** Body for `POST /v1/cms/arena-clan-tournaments/save`. Editable until settlement freezes the board. */
export const SaveCmsArenaClanTournamentBodySchema = z
  .object({
    id: z.number().int().positive().optional(),
    clanId: z.number().int().positive(),
    name: z.string().trim().min(1).max(64),
    startsAt: z.coerce.date(),
    endsAt: z.coerce.date(),
    rankingMode: ArenaClanTournamentRankingSchema,
    rewardBreakdown: ArenaClanRewardBreakdownSchema,
  })
  .refine((body) => body.endsAt.getTime() > body.startsAt.getTime(), { error: "the end must be after the start" });
export type SaveCmsArenaClanTournamentBody = z.infer<typeof SaveCmsArenaClanTournamentBodySchema>;

/** Response for `GET /v1/cms/arena-clan-tournaments/get-by-page`: latest start first. */
export const ListCmsArenaClanTournamentsResponseSchema = z.object({
  items: CmsArenaClanTournamentDtoSchema.array(),
  total: z.number().int().nonnegative(),
  page: z.number().int().nonnegative(),
  size: z.number().int().positive(),
});
export type ListCmsArenaClanTournamentsResponse = z.infer<typeof ListCmsArenaClanTournamentsResponseSchema>;
